// The look's colour maths (character look, 2026-10-29). Pure: the uniforms LookPipeline sends, the fragment shader it
// runs, and mapColour — the same gradient map in JS (tests, CPU portraits). The shader and mapColour share LUMA and
// T_MAX below; glslMapColour is the shader's ramp() line for line, and a test keeps the two in step.
//
// A region (skin, hair) is recoloured by luminance: the art's own shadow / mid / light luminances (the body's default
// palette) map onto the chosen colour's shadow / mid / light stops, extrapolated above the light stop so highlights
// survive. Skin also keeps what the art painted over its own ramp (blush, lips, warm shading, line art): the pixel's
// per-channel ratio to the art ramp at its luminance multiplies the new colour, so the art's own tone maps to itself.
// Iris: a dark→light gradient by luminance. Brow: one colour scaled by the pixel's luminance ratio.
import { LOOK_CATALOG, resolveLook, isDefaultLook } from './lookCatalog.mjs';

export const LUMA = [0.299, 0.587, 0.114];
export const T_MAX = 1.25;              // how far past the light stop a highlight may go
const BROW_MAX = 1.5;

export const hexRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
export const luma = (rgb) => rgb[0] * LUMA[0] + rgb[1] * LUMA[1] + rgb[2] * LUMA[2];

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const mix = (a, b, t) => a.map((v, i) => v + (b[i] - v) * t);
const byId = (list, id) => list.find((e) => e.id === id);
const stops = (e) => [hexRgb(e.shadow), hexRgb(e.mid), hexRgb(e.light)];

function ramp(entry, art, on) {
  return { on, stops: stops(entry), ref: stops(art).map(luma), art: stops(art) };
}

/** The shader uniforms for this look, or null when it draws exactly like the art. */
export function lookUniforms(character, look) {
  if (isDefaultLook(character, look)) return null;
  const r = resolveLook(character, look);
  const d = LOOK_CATALOG.defaults[LOOK_CATALOG.defaults[character] ? character : 'natalka'];
  const hairOn = r.hairColor !== d.hairColor;
  const eye = byId(LOOK_CATALOG.eyes, r.eyes), artEye = byId(LOOK_CATALOG.eyes, d.eyes);
  const hair = byId(LOOK_CATALOG.hairColor, r.hairColor), artHair = byId(LOOK_CATALOG.hairColor, d.hairColor);
  return {
    skin: ramp(byId(LOOK_CATALOG.skin, r.skin), byId(LOOK_CATALOG.skin, d.skin), r.skin !== d.skin),
    hair: ramp(hair, artHair, hairOn),
    iris: { on: r.eyes !== d.eyes, dark: hexRgb(eye.dark), light: hexRgb(eye.light),
      ref: [luma(hexRgb(artEye.dark)), luma(hexRgb(artEye.light))] },
    brow: { on: hairOn, colour: hexRgb(hair.brow), ref: luma(hexRgb(artHair.brow)) },
  };
}

/** One pixel (sRGB 0..1, straight alpha) through a skin / hair region's gradient map. */
export function mapColour(rgb, region) {
  const l = luma(rgb);
  const [s0, s1, s2] = region.stops, [lo, mid, hi] = region.ref;
  if (l <= mid) return mix(s0, s1, clamp((l - lo) / (mid - lo), 0, 1));
  return mix(s1, s2, clamp((l - mid) / (hi - mid), 0, T_MAX)).map((v) => clamp(v, 0, 1));
}

const RATIO_MAX = 2;

/** One skin pixel: mapColour times the pixel's ratio to the art's own ramp (identity on the art's tone). */
export function mapSkin(rgb, region) {
  const art = mapColour(rgb, { ...region, stops: region.art });
  return mapColour(rgb, region).map((v, i) => clamp(v * clamp(rgb[i] / Math.max(art[i], 1e-3), 0, RATIO_MAX), 0, 1));
}

/** One eye pixel. Lighter than the art eye (the middle of its two stops) is part skin, the border blending its dark core
 *  into the face: `a` is the eye's share. A new eye colour fades into the new skin there; default eyes keep the art and move only the skin share.
 *  Darker than the art eye's dark stop (lashes, pupil, the eye's outline) the new dark stop darkens with it (gate 4b). */
function eyePixel(rgb, l, u) {
  const a = clamp((u.skin.ref[1] - l) / Math.max(u.skin.ref[1] - (u.iris.ref[0] + u.iris.ref[1]) / 2, 0.05), 0, 1);
  const skinMid = u.skin.on ? u.skin.stops[1] : u.skin.art[1];
  if (!u.iris.on) return rgb.map((v, i) => clamp(v + (1 - a) * (skinMid[i] - u.skin.art[1][i]), 0, 1));
  const eye = l < u.iris.ref[0] ? u.iris.dark.map((v) => v * (l / u.iris.ref[0]))
    : mix(u.iris.dark, u.iris.light, clamp((l - u.iris.ref[0]) / (u.iris.ref[1] - u.iris.ref[0]), 0, 1));
  return mix(skinMid, eye, a);
}

/** One pixel (sRGB 0..1, straight alpha) in a look: the shader's main() for region weights `w` (maskWeights). */
export function lookPixel(rgb, w, u) {
  const l = luma(rgb);
  let c = rgb;
  if (u.skin.on && w.skin) c = mix(c, mapSkin(rgb, u.skin), w.skin);
  if (u.hair.on && w.hair) c = mix(c, mapColour(rgb, u.hair), w.hair);
  if ((u.iris.on || u.skin.on) && w.iris) c = mix(c, eyePixel(rgb, l, u), w.iris);
  if (u.brow.on && w.brow) c = mix(c, u.brow.colour.map((v) => clamp(v * clamp(l / u.brow.ref, 0, BROW_MAX), 0, 1)), w.brow);
  return c;
}

// ---- the shader ----

/** The shader's ramp(), transliterated (vec3 maths as arrays) — kept beside the GLSL so a test pins the two. */
export function glslMapColour(rgb, region) {
  const l = rgb[0] * LUMA[0] + rgb[1] * LUMA[1] + rgb[2] * LUMA[2];
  const [s0, s1, s2] = region.stops, ref = region.ref;
  if (l <= ref[1]) return mix(s0, s1, clamp((l - ref[0]) / (ref[1] - ref[0]), 0.0, 1.0));
  return mix(s1, s2, clamp((l - ref[1]) / (ref[2] - ref[1]), 0.0, T_MAX)).map((v) => clamp(v, 0.0, 1.0));
}

/** The shader's skin(), transliterated. */
export function glslMapSkin(rgb, region) {
  const art = glslMapColour(rgb, { ...region, stops: region.art });
  return glslMapColour(rgb, region).map((v, i) => clamp(v * clamp(rgb[i] / Math.max(art[i], 0.001), 0.0, RATIO_MAX), 0.0, 1.0));
}

// ---- the mask ----
// A mask file (scripts/anim/look_masks.py) holds one 4-bit code per atlas pixel, six pixels per texel: pixel 6t + 2c
// is the high nibble of channel c of texel t, 6t + 2c + 1 the low one. Codes: 0 none, 1-5 skin k/5, 6-10 hair
// (k-5)/5, 11-12 iris (k-10)/2, 13-15 brow (k-12)/3. A sixth of a full RGB mask, at full resolution (Task 5 ruling).

/** The regions' weights for one code. An eye (11, 12) is one region: eyePixel finds its skin share by luminance. A brow
 *  edge (13, 14) is skin underneath, the skin map keeping its dark part dark by the art's own ratio; its core (15) stays
 *  as drawn. */
export function maskWeights(code) {
  const iris = code >= 11 && code <= 12 ? 1 : 0, brow = code >= 13 ? (code - 12) / 3 : 0;
  return {
    skin: code >= 1 && code <= 5 ? code / 5 : (code === 13 || code === 14 ? 1 : 0),
    hair: code >= 6 && code <= 10 ? (code - 5) / 5 : 0,
    iris,
    brow,
  };
}

/** The code of atlas pixel (x, y) from the mask's RGBA bytes (getImageData), `maskW` texels wide. */
export function maskCodeAt(bytes, maskW, x, y) {
  const k = x % 6, byte = bytes[(y * maskW + (x - k) / 6) * 4 + (k >> 1)];
  return k & 1 ? byte & 15 : byte >> 4;
}

/** The shader's maskCode(), transliterated: texture coordinate u of an `atlasW`-wide atlas, row `y` of the mask. */
export function glslMaskCode(bytes, maskW, atlasW, u, y = 0) {
  const floor = Math.floor;
  const px = floor(u * atlasW);
  const t = floor((px + 0.5) / 6.0);
  const k = px - t * 6.0;
  const texel = floor(((t + 0.5) / maskW) * maskW);
  const m = [0, 1, 2].map((c) => bytes[(y * maskW + texel) * 4 + c] / 255);
  const c = floor((k + 0.5) / 2.0);
  const v = c < 0.5 ? m[0] : (c < 1.5 ? m[1] : m[2]);
  const byte = floor(v * 255.0 + 0.5);
  const hi = floor((byte + 0.5) / 16.0);
  return (k - c * 2.0) < 0.5 ? hi : byte - hi * 16.0;
}

const f = (n) => (Number.isInteger(n) ? `${n}.0` : `${n}`);

// Phaser's Single shader with the recolour before the tint. The mask (codes, above) is sampled with NEAREST at the
// texel holding this atlas pixel; uMaskOn is 0 when the sprite's texture has no mask (yet). Textures are premultiplied: un-premultiply, map, re-multiply.
export const LOOK_FRAG = `
#define SHADER_NAME LOOK_FS
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform sampler2D uMainSampler;
uniform sampler2D uMaskSampler;
uniform vec3 uSkin0; uniform vec3 uSkin1; uniform vec3 uSkin2; uniform vec3 uSkinRef; uniform float uSkinOn;
uniform vec3 uSkinA0; uniform vec3 uSkinA1; uniform vec3 uSkinA2;
uniform vec3 uHair0; uniform vec3 uHair1; uniform vec3 uHair2; uniform vec3 uHairRef; uniform float uHairOn;
uniform vec3 uIrisDark; uniform vec3 uIrisLight; uniform vec2 uIrisRef; uniform float uIrisOn;
uniform vec3 uBrow; uniform float uBrowRef; uniform float uBrowOn;
uniform float uAtlasW;
uniform float uMaskW;
uniform float uMaskOn;

varying vec2 outTexCoord;
varying float outTintEffect;
varying vec4 outTint;

const vec3 LUMA = vec3(${LUMA.map(f).join(', ')});
const float T_MAX = ${f(T_MAX)};
const float RATIO_MAX = ${f(RATIO_MAX)};

vec3 ramp (float l, vec3 s0, vec3 s1, vec3 s2, vec3 ref)
{
    if (l <= ref.y) return mix(s0, s1, clamp((l - ref.x) / (ref.y - ref.x), 0.0, 1.0));
    return clamp(mix(s1, s2, clamp((l - ref.y) / (ref.z - ref.y), 0.0, T_MAX)), 0.0, 1.0);
}

vec3 skinMap (vec3 c, float l)
{
    vec3 art = ramp(l, uSkinA0, uSkinA1, uSkinA2, uSkinRef);
    return clamp(ramp(l, uSkin0, uSkin1, uSkin2, uSkinRef) * clamp(c / max(art, vec3(0.001)), 0.0, RATIO_MAX), 0.0, 1.0);
}

vec3 eyePixel (vec3 c, float l)
{
    float a = clamp((uSkinRef.y - l) / max(uSkinRef.y - (uIrisRef.x + uIrisRef.y) * 0.5, 0.05), 0.0, 1.0);
    vec3 skinMid = uSkinOn > 0.5 ? uSkin1 : uSkinA1;
    if (uIrisOn < 0.5) return clamp(c + (1.0 - a) * (skinMid - uSkinA1), 0.0, 1.0);
    vec3 eye = l < uIrisRef.x ? uIrisDark * (l / uIrisRef.x)
        : mix(uIrisDark, uIrisLight, clamp((l - uIrisRef.x) / (uIrisRef.y - uIrisRef.x), 0.0, 1.0));
    return mix(skinMid, eye, a);
}

float maskCode ()
{
    float px = floor(outTexCoord.x * uAtlasW);
    float t = floor((px + 0.5) / 6.0);
    float k = px - t * 6.0;
    vec4 m = texture2D(uMaskSampler, vec2((t + 0.5) / uMaskW, outTexCoord.y));
    float c = floor((k + 0.5) / 2.0);
    float v = c < 0.5 ? m.r : (c < 1.5 ? m.g : m.b);
    float byte = floor(v * 255.0 + 0.5);
    float hi = floor((byte + 0.5) / 16.0);
    return (k - c * 2.0) < 0.5 ? hi : byte - hi * 16.0;
}

void main ()
{
    vec4 tex = texture2D(uMainSampler, outTexCoord);
    float code = uMaskOn > 0.5 ? maskCode() : 0.0;
    float hair = (code > 5.5 && code < 10.5) ? (code - 5.0) / 5.0 : 0.0;
    float iris = (code > 10.5 && code < 12.5) ? 1.0 : 0.0;
    float brow = code > 12.5 ? (code - 12.0) / 3.0 : 0.0;
    float skin = (code > 0.5 && code < 5.5) ? code / 5.0 : (code > 12.5 && code < 14.5 ? 1.0 : 0.0);
    vec3 c = tex.a > 0.0 ? tex.rgb / tex.a : tex.rgb;
    float l = dot(c, LUMA);
    c = mix(c, skinMap(c, l), skin * uSkinOn);
    c = mix(c, ramp(l, uHair0, uHair1, uHair2, uHairRef), hair * uHairOn);
    c = mix(c, eyePixel(c, l), iris * max(uIrisOn, uSkinOn));
    c = mix(c, clamp(uBrow * clamp(l / uBrowRef, 0.0, ${f(BROW_MAX)}), 0.0, 1.0), brow * uBrowOn);
    vec4 texture = vec4(c * tex.a, tex.a);

    vec4 texel = vec4(outTint.bgr * outTint.a, outTint.a);
    vec4 color = texture * texel;
    if (outTintEffect == 1.0)
    {
        color.rgb = mix(texture.rgb, outTint.bgr * outTint.a, texture.a);
    }
    else if (outTintEffect == 2.0)
    {
        color = texel;
    }
    gl_FragColor = color;
}
`;
